import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import cn from 'classnames';
import { Button, Header, Icon, Popup } from 'semantic-ui-react';
import Fade from 'react-reveal/Fade';
import { StockExposure as StockAction, Accounts } from 'actions';
import { Segment, SearchTicker, Button as ButtonIcon } from 'components';
import { colors } from 'utils/colors';
import Showcase from './Showcase';
import MultiSelect from '../../../components/Multiselect';

class StockExposure extends Component {
  constructor(props) {
    super(props);
    this.state = {
      headerfixed: false,
      selectedAccountIds: [],
      displayedAccountIds: [],
      transformedAccounts: [],
      title: '',
      stockError: '',
      accountError: '',
      initializing: true, // avoids extra re-render when selectedAccountIds is updated after fetching is complete
      togglePct: true,
      fromOverview: true,
    };
    this.setSelectedAccounts = this.setSelectedAccounts.bind(this);
  }

  componentWillMount() {
    const { accountData, dispatch, stockExposure } = this.props;
    const { fromOverview } = this.state;
    const preSelectedAccountIds = stockExposure.preSelectedAccounts || [];
    if (preSelectedAccountIds.length > 0 && fromOverview) {
      this.setState({ fromOverview: false });
    }

    if (!accountData) {
      dispatch(Accounts.fetchAllData()).then((accounts) => {
        this.initializeData(accounts, preSelectedAccountIds);
      });
    } else {
      this.initializeData(accountData, preSelectedAccountIds);
    }
  }

  componentDidUpdate = (prevProps, prevState) => {
    const { accountData, stockExposure } = this.props;
    const { stockError, accountError, fromOverview } = this.state;
    const preSelectedAccountIds = stockExposure.preSelectedAccounts || [];
    if (preSelectedAccountIds.length > 0 && fromOverview) {
      this.setState({ fromOverview: false });
    }

    if (accountData && accountData !== prevProps.accountData) {
      this.initializeData(accountData, preSelectedAccountIds);
    }

    if (stockExposure.accounts !== prevProps.stockExposure.accounts) {
      // could be different because of an error calling the stock exposure endpoint,
      // so check that there is a ticker and that call was successful before updating.
      if (stockExposure.tickers[0]) {
        this.handleChange('title', stockExposure.tickers[0]);
      }
      if (stockExposure.accounts.lenth === 0 && stockExposure.tickers[0]) {
        this.setSelectedAccounts(stockExposure.accounts);
      }
    }

    if (
      stockError !== prevState.stockError ||
      accountError !== prevState.accountError
    ) {
      this.render();
    }
  };

  getResults = () => {
    const { search } = this.props.stockExposure;

    return search.reduce((total, security) => {
      if (security) {
        if (!total.STOCK) {
          total.STOCK = {
            name: 'STOCK',
            results: [
              {
                title: security.ticker,
                description: security.name,
                price: security.exchange,
              },
            ],
          };
        } else {
          total.STOCK.results.push({
            title: security.ticker,
            description: security.name,
            price: security.exchange,
          });
        }
      }
      return total;
    }, {});
  };

  setSelectedAccounts = (selectedAccountIds) => {
    this.setState({ selectedAccountIds });
    this.setState({ accountError: '' });
  };

  handleChange = (name, value) => this.setState({ [name]: value });

  handleTogglePct = (togglePct) => {
    this.setState({ togglePct });
  };

  handleResultSelect = (title) => {
    this.setState({ title });
    this.setState({ stockError: '' });
  };

  handleNewSelection = (newAccountIds) => {
    const { dispatch } = this.props;
    const { title } = this.state;

    dispatch(StockAction.fetch([title], newAccountIds));
    dispatch(StockAction.addPrevSearch(title));
  };

  handleSearchChange = (value) => {
    const { dispatch } = this.props;

    return (
      !!value.trim() &&
      dispatch(StockAction.search(window.encodeURIComponent(value)))
    );
  };

  handleScroll = () => {
    const { stockExposure } = this.props;

    if (!Object.keys(stockExposure).length) return;
    if (window.scrollY >= 100) {
      this.setState({ headerfixed: true });
    } else {
      this.setState({ headerfixed: false });
    }
  };

  transformAccountDataForMultiSelect = (accountData) =>
    accountData.map((account) => {
      const details = [account.user_institution, account.label];
      const joined = details.join(': ');
      return {
        id: account.id,
        name: joined,
      };
    });

  initializeData = (accountData, preSelectedAccountIds = []) => {
    const { stockExposure } = this.props;
    const accountIds = accountData.map((account) => account.id);

    this.handleChange(
      'transformedAccounts',
      this.transformAccountDataForMultiSelect(accountData)
    );

    // If we previously calculated a stock exposure and still have those results,
    // we want to display/select the specific accounts used in the calculation in the multiselect dropdown.
    // e.g. when coming from the holdings concentration tool, we call the stock exposure lookup from that file with
    // the given ticker and selected accounts.
    if (preSelectedAccountIds.length > 0) {
      this.setState({
        selectedAccountIds: preSelectedAccountIds,
        displayedAccountIds: preSelectedAccountIds,
        initializing: false,
      });
    } else if (stockExposure.accounts.length > 0) {
      this.setState({
        title: stockExposure.tickers[0], // we only search one ticker in the endpoint, but it must be sent as an array
        selectedAccountIds: stockExposure.accounts,
        displayedAccountIds: stockExposure.accounts,
        initializing: false,
      });
    } else {
      this.setState({
        selectedAccountIds: accountIds,
        initializing: false,
      });
    }
  };

  generatePDF = () => {
    const { history } = this.props;
    const { togglePct } = this.state;
    // Pass togglePct as state to PDF route
    return history.push({
      pathname: '/tools/stock-exposure/pdf',
      state: { togglePct },
    });
  };

  fetchExposure = () => {
    const { dispatch } = this.props;
    const { title, selectedAccountIds } = this.state;

    if (title.length > 0 && selectedAccountIds.length > 0) {
      dispatch(StockAction.fetch([title], selectedAccountIds));
      dispatch(StockAction.addPrevSearch(title));
      this.setState({ displayedAccountIds: selectedAccountIds });
    }
    if (title.length === 0) {
      this.setState({ stockError: 'Please select a stock below.' });
    }
    if (selectedAccountIds.length === 0) {
      this.setState({ accountError: 'Please select at least one account.' });
    }
  };

  renderAccountChips = () => {
    const { displayedAccountIds, transformedAccounts } = this.state;
    const { accountData } = this.props;

    if (!accountData) return null;

    const aggregationIds = accountData
      .filter((a) => a.for_aggregation)
      .map((a) => a.id);

    const isAllAccounts =
      aggregationIds.length > 0 &&
      aggregationIds.length === displayedAccountIds.length &&
      aggregationIds.every((id) => displayedAccountIds.includes(id));

    if (isAllAccounts) return null;

    const selectedLabels = displayedAccountIds
      .map((id) => {
        const match = transformedAccounts.find((a) => a.id === id);
        return match ? match.name : null;
      })
      .filter(Boolean);

    return selectedLabels;
  };

  render() {
    const { stockExposure, isPrinting, accountData } = this.props;
    const { prevSearch } = stockExposure;
    const showSecurityContent =
      stockExposure &&
      stockExposure.data &&
      Object.keys(stockExposure.data.total_direct_exposures).length !== 0 &&
      stockExposure.data.total_direct_exposures !== null;
    const accountsAreEmpty =
      stockExposure &&
      stockExposure.data &&
      Object.keys(stockExposure.data.total_direct_exposures).length === 0;
    const {
      headerfixed,
      selectedAccountIds,
      displayedAccountIds,
      transformedAccounts,
      initializing,
      title,
      stockError,
      accountError,
      fromOverview,
    } = this.state;

    const accountRegion =
      displayedAccountIds.length === 0 ||
      accountData.some(
        (a) => displayedAccountIds.includes(a.id) && a.currency === 'CAD'
      )
        ? 'CA'
        : 'US';

    return (
      <Segment
        margin={!isPrinting ? '1rem' : '2rem 3rem 2rem 3rem'}
        padding="0rem"
        textAlign="center"
        style={{
          position: 'relative',
          minHeight: '700px',
          overflowX: 'visible',
        }}
      >
        <HeaderContainer
          showSecurityContent={showSecurityContent}
          scrollYPos={window.scrollY}
          headerfixed={headerfixed}
        >
          <HeaderFormat>
            <div className="header">
              <Header
                size="large"
                textAlign="left"
                style={{ margin: 0, width: '100%' }}
              >
                <Header.Content style={{ marginBottom: '1rem' }}>
                  Stock Exposure Lookup
                </Header.Content>
                <Header.Subheader style={{ width: '100%' }}>
                  Enter a ticker or name, then click Lookup to see this client's
                  direct and indirect exposure in the selected accounts,
                  including exposure via funds and ETFs.
                </Header.Subheader>
                {(() => {
                  const chips = this.renderAccountChips();
                  const MAX_CHIPS = 3;
                  return (
                    <AccountChipsRow>
                      <span className="showing-label">
                        <Icon
                          name="book"
                          style={{
                            fontSize: '15px',
                            verticalAlign: 'initial',
                            color: 'black',
                            marginRight: 0,
                          }}
                        />
                        Showing:
                      </span>
                      {chips === null ? (
                        <span className="chip">All accounts</span>
                      ) : chips.length === 0 ? null : (
                        <React.Fragment>
                          {chips
                            .slice(
                              0,
                              chips.length > 4 ? MAX_CHIPS : chips.length
                            )
                            .map((label) => (
                              <span key={label} className="chip">
                                {label}
                              </span>
                            ))}
                          {chips.length > 4 && (
                            <span className="chip-overflow">
                              +{chips.length - MAX_CHIPS} more
                            </span>
                          )}
                        </React.Fragment>
                      )}
                    </AccountChipsRow>
                  );
                })()}
              </Header>
            </div>
            {!isPrinting && fromOverview && (
              <div className="account-select">
                <div className="select">
                  <MultiSelect
                    items={transformedAccounts}
                    selectedItems={selectedAccountIds}
                    setSelectedItems={this.setSelectedAccounts}
                    placeholder="Select account(s)"
                    maxHeight="300px"
                  />
                  {accountError.length > 0 && (
                    <div style={{ fontSize: '15px' }}>
                      <Icon
                        name="warning sign"
                        style={{ color: colors.red, marginTop: '5px' }}
                      />
                      <span style={{ color: colors.red }}>{accountError} </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </HeaderFormat>

          {/* {!isPrinting && (
            <ButtonIcon
              className={cn(
                'left',
                !prevSearch || stockExposure.isFetching || initializing
                  ? 'disabled'
                  : ''
              )}
              icon="file text"
              color={colors.darkGrey}
              onClick={this.generatePDF}
              style={{
                position: 'absolute',
                top: '1.5rem',
                right: '3.3rem',
              }}
            >
              Print/PDF
            </ButtonIcon>
          )} */}

          {!isPrinting && (
            <div
              style={{
                textAlign: 'left',
                padding: '2rem 0 0 12rem ',
                fontSize: '15px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                columnGap: '10px',
              }}
            >
              {title ? <b>Selected Stock: </b> : <b>Select a Stock</b>}{' '}
              {stockError.length === 0 ? (
                <b style={{ color: colors.blue }}>{title}</b>
              ) : (
                <div
                  style={{
                    fontSize: '15px',
                  }}
                >
                  <Icon
                    name="warning sign"
                    style={{
                      color: colors.red,
                      marginBottom: '2px',
                    }}
                  />
                  <span style={{ color: colors.red }}>{stockError} </span>
                </div>
              )}
            </div>
          )}
          {!isPrinting && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                columnGap: '1rem',
                justifyContent: 'left',
                alignItems: 'center',
                marginTop: '0.5rem',
                paddingLeft: '12rem',
              }}
            >
              <SearchTicker
                placeholder="Search for ticker or name"
                results={this.getResults()}
                customResultSelect={this.handleResultSelect}
                customSearchChange={this.handleSearchChange}
                left
                disabled={stockExposure.isFetching}
              />
              <Button
                onClick={() => this.fetchExposure()}
                style={{
                  background: colors.periwinkle,
                  color: 'white',
                }}
                disabled={stockExposure.isFetching}
              >
                Lookup
              </Button>
            </div>
          )}

          {accountsAreEmpty ? (
            <WarningMessage>
              <Icon name="warning sign" /> This tool requires you to have saved
              accounts in Wealthscope. <br /> Please Upload, Manually Enter or
              Link your Accounts to get started.
            </WarningMessage>
          ) : null}
        </HeaderContainer>

        {prevSearch ? (
          <ContentContainer>
            <Fade duration={!isPrinting ? 3500 : 0}>
              <Showcase
                isPrinting={isPrinting}
                togglePct={this.state.togglePct}
                onTogglePct={this.handleTogglePct}
                accountRegion={accountRegion}
                fromOverview={fromOverview}
              />
            </Fade>
            {!stockExposure.isFetching &&
              !initializing &&
              (!isPrinting ? (
                <Popup
                  trigger={<LinkButton>Methodology</LinkButton>}
                  wide
                  position="top center"
                  style={{ maxWidth: '650px' }}
                >
                  For funds, the calculations are based on their 25 top
                  holdings. Note that a fund's holdings and weights may have
                  changed since its last quarterly SEC/SEDAR filing. Further due
                  diligence is recommended.
                </Popup>
              ) : (
                <div
                  style={{
                    margin: 'auto',
                    marginTop: '1rem',
                    maxWidth: '60%',
                  }}
                >
                  For funds, the calculations are based on their 25 top
                  holdings. Note that a fund's holdings and weights may have
                  changed since its last quarterly SEC/SEDAR filing. Further due
                  diligence is recommended.
                </div>
              ))}
          </ContentContainer>
        ) : null}
      </Segment>
    );
  }
}

StockExposure.propTypes = {
  dispatch: PropTypes.func.isRequired,
  stockExposure: PropTypes.object.isRequired,
  accountData: PropTypes.array,
  isPrinting: PropTypes.bool,
  history: PropTypes.object,
};

StockExposure.defaultProps = {
  accountData: [],
  isPrinting: false,
  history: {},
};

export default connect((state) => ({
  stockExposure: state.StockExposure,
  inputs: state.CorrelationMatrix.inputs,
  data: state.CorrelationMatrix.data,
  analytics: state.Analytics,
  user: state.Auth.user,
  user_region: localStorage.getItem('user_region'),
  accountData: state.Accounts.accountList,
}))(StockExposure);

const HeaderContainer = styled(({ className, children, ...rest }) => (
  <div className={className} {...rest}>
    {children}
  </div>
))`
  transition: transform 1s;
  background-color: white;
  z-index: 2;
  width: 100%;
  padding-bottom: 30px;
`;

const ContentContainer = styled(
  ({ className, children, hasErrorMessage, ...rest }) => (
    <div className={className} {...rest}>
      {children}
    </div>
  )
)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const WarningMessage = styled.div`
  color: ${colors.red};
  font-weight: bold;
  margin: 2rem 0 0 0;
`;

const LinkButton = styled.button`
  border: none;
  color: ${colors.blue};
  background: white;
  font-weight: bold;
  margin-top: 3rem;

  &: hover {
    text-decoration: underline;
  }
`;

const HeaderFormat = styled.div`
  display: flex;
  flex-direction: row;
  text-align: left;
  align-items: start;
  column-gap: 2rem;
  width: 100%;
  padding-bottom: 2rem;
  border-bottom: 1px solid #ccc;
  .header {
    width: 60%;
  }

  .account-select {
    width: 40%;
    display: flex;
    justify-content: end;
    align-self: end;

    .select {
      width: 100%;
      max-width: 400px;
      .accounts-select {
        display: flex;
        flex-direction: row;
        column-gap: 5px;
      }
    }
  }
`;

const AccountChipsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 0.5rem;
  margin-bottom: 1rem;
  font-size: 14px;
  color: ${colors.darkGrey};

  .showing-label {
    display: flex;
    gap: 6px;
    color: ${colors.darkGrey};
  }

  .chip {
    background: #f0f2f5;
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 14px;
    color: #333;
    white-space: nowrap;
  }

  .chip-overflow {
    background: #dce4f5;
    color: ${colors.navyBlue};
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 14px;
    white-space: nowrap;
  }
`;
